import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack } from "@mzc-pdc/ui";
import { IconSchema } from "@mzc-cloudplex/icons";
import theme from "@styles/theme";
import { METADATA_FIELDS_TYPE, PROMPT_PRESET_RESPONSE_FORMATS, PROMPT_PRESET_RESPONSE_FORMAT_CODE } from "@constants";
import useCustomContent from "@hooks/useCustomContent";
import useDialog from "@hooks/useDialog";
import PanelPropertyTitle from "@components/view/components/panel-property-title";
import MetadataFieldReadMode from "@features/metadata-fields/metadata-field-read-mode";
import MetadataFieldEditMode from "@features/metadata-fields/metadata-field-edit-mode";
import FieldEditBox from "@features/field-by-type/field-edit-box";
import DialogSelectCustomMetadata from "@features/dialog/custom-metadata/dialog-select-custom-metadata";
import { CustomMetadataSchemaCard } from "@features/custom-metadata";

/**
 * 프롬프트 프리셋 상세 패널의 "응답형태" 복합 필드 (읽기 + 편집).
 *
 * 응답형태 = 커스텀 메타데이터인 경우 스키마 정의 카드를 표시(읽기)하고,
 * 편집 시 응답형태 드롭다운 + 커스텀 메타데이터 선택 모달을 통해 응답형태와 contentSchemaId를 함께 저장한다.
 * (설명/프롬프트는 기존 generic PanelMetadata가 담당하므로 여기서 다루지 않는다.)
 *
 * @param {object} props
 * @param {object} props.data propertyPanel.data (프리셋 상세). responseFormatCode, responseFormat.jsonSchema.contentSchemaId,
 *   그리고 getPromptPreset이 함께 조회해 둔 contentSchemaDetail(raw content schema)을 사용
 * @param {string} props.projectId
 * @param {boolean} props.isEditButtonDisabled 시스템 관리/타 프로젝트 프리셋 등 읽기 전용일 때 true
 * @param {(payload:{responseFormatCode:string, contentSchemaId?:string}) => Promise<void>} props.onSubmit 저장 핸들러
 */
const PanelPromptPresetResponseFormat = ({ data, projectId, isEditButtonDisabled, onSubmit }) => {
    const { t } = useTranslation();
    const { getCustomMetadataByContentSchema } = useCustomContent();
    const { dialog, openDialog, closeDialog } = useDialog();

    const responseFormatCode = data?.responseFormatCode;
    const appliedContentSchemaId = data?.responseFormat?.jsonSchema?.contentSchemaId;

    const [isEdit, setIsEdit] = useState(false);
    const [editResponseFormatCode, setEditResponseFormatCode] = useState(responseFormatCode);
    const [editContentSchemaDetail, setEditContentSchemaDetail] = useState(null);
    const [pending, setPending] = useState(false);

    const isEditCustomMetadata = editResponseFormatCode === PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA;
    const noChange =
        editResponseFormatCode === responseFormatCode &&
        (!isEditCustomMetadata || editContentSchemaDetail?.id === appliedContentSchemaId);
    const submitDisabled = pending || noChange || (isEditCustomMetadata && !editContentSchemaDetail?.id);

    const responseFormatOptions = useMemo(
        () =>
            PROMPT_PRESET_RESPONSE_FORMATS.map(({ label, value }) => ({
                id: value,
                value,
                label: t(`common::label::${label}`, label),
            })),
        [t],
    );

    // getPromptPreset/updatePromptPreset가 프리셋과 함께 조회해 둔 content schema 상세(raw)를 카드용으로 정규화한다.
    // contentSchemaDetail은 커스텀 메타데이터일 때만 존재(그 외 null)하므로, 커스텀 여부 분기 없이 유무만으로 판단한다.
    const schemaDetail = useMemo(
        () => (data?.contentSchemaDetail ? getCustomMetadataByContentSchema(data.contentSchemaDetail) : null),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [data?.contentSchemaDetail],
    );

    const handleEnterEdit = useCallback(() => {
        setEditResponseFormatCode(responseFormatCode);
        setEditContentSchemaDetail(schemaDetail); // schemaDetail은 커스텀이 아니면 null
        setIsEdit(true);
    }, [responseFormatCode, schemaDetail]);

    const handleCloseEdit = useCallback(() => {
        setIsEdit(false);
    }, []);

    const handleChangeFormat = useCallback((_id, value) => {
        setEditResponseFormatCode(value);
        if (value !== PROMPT_PRESET_RESPONSE_FORMAT_CODE.CUSTOM_METADATA) {
            setEditContentSchemaDetail(null);
        }
    }, []);

    const handleSubmit = useCallback(async () => {
        setPending(true);
        try {
            await onSubmit({
                responseFormatCode: editResponseFormatCode,
                contentSchemaId: editContentSchemaDetail?.id,
            });
            setIsEdit(false);
        } catch (e) {
            console.error(e);
        } finally {
            setPending(false);
        }
    }, [onSubmit, editResponseFormatCode, editContentSchemaDetail]);

    // 다른 프리셋으로 전환 시 편집 상태 초기화
    useEffect(() => {
        setIsEdit(false);
    }, [data?.id]);

    return (
        <>
            <Box sx={{ pl: 2.5, pr: 1, pb: 2.5 }}>
                <Box sx={{ px: 1.5, py: 0.25 }}>
                    <PanelPropertyTitle
                        variant={"body2"}
                        color={theme.palette.greySecondary[600]}
                        subtitle={t(`common::label::Response format`, `Response format`)}
                        required
                        showEditButton={!isEditButtonDisabled && !isEdit}
                        editButtonDisabled={isEditButtonDisabled}
                        onClickEditButton={handleEnterEdit}
                    />

                    {!isEdit && (
                        <>
                            <Box pt={1}>
                                <MetadataFieldReadMode
                                    type={METADATA_FIELDS_TYPE.SINGLE_SELECT}
                                    isHidden={false}
                                    value={responseFormatCode}
                                    options={responseFormatOptions}
                                />
                            </Box>
                            {schemaDetail && (
                                <Box pt={1.5}>
                                    <CustomMetadataSchemaCard
                                        variant={"definition"}
                                        schemaName={schemaDetail.name}
                                        fields={schemaDetail.fields}
                                    />
                                </Box>
                            )}
                        </>
                    )}

                    {isEdit && (
                        <Box pt={1}>
                            <FieldEditBox
                                submitDisabled={submitDisabled}
                                submitPending={pending}
                                onClickSubmit={handleSubmit}
                                onClickClose={handleCloseEdit}
                            >
                                <Stack direction={"column"} gap={1}>
                                    <MetadataFieldEditMode
                                        id={"responseFormatCode"}
                                        type={METADATA_FIELDS_TYPE.SINGLE_SELECT}
                                        value={editResponseFormatCode}
                                        options={responseFormatOptions}
                                        isEdit
                                        onChangeField={handleChangeFormat}
                                    />
                                    {isEditCustomMetadata && (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "flex-start",
                                                gap: 1,
                                                width: "100%",
                                                border: `1px solid ${theme.palette.greySecondary[100]}`,
                                                borderRadius: theme.spacing(0.5),
                                                p: 1.5,
                                            }}
                                        >
                                            <Button
                                                color={"greySecondary"}
                                                variant={"outlined"}
                                                size={"small"}
                                                type={"button"}
                                                startIcon={<IconSchema size={16} />}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDialog("selectCustomMetadata");
                                                }}
                                            >
                                                {t(`common::label::Custom Metadata`, `Custom Metadata`)}
                                            </Button>
                                            {editContentSchemaDetail && (
                                                <CustomMetadataSchemaCard
                                                    variant={"definition"}
                                                    hideFields
                                                    schemaName={editContentSchemaDetail.name}
                                                    fields={editContentSchemaDetail.fields}
                                                />
                                            )}
                                        </Box>
                                    )}
                                </Stack>
                            </FieldEditBox>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* warningMessage 미전달: 프리셋은 스키마를 참조만 해 입력된 값이 없으므로 "데이터 삭제" 경고가 불필요 */}
            {dialog === "selectCustomMetadata" && (
                <DialogSelectCustomMetadata
                    projectId={projectId}
                    contentSchemaId={appliedContentSchemaId}
                    showSelectedSchemaInfo
                    guideMessage={t(
                        `common::msg::After AI analyzes the content, the field values of the selected content schema are automatically filled in. Only one schema can be selected.`,
                        `After AI analyzes the content, the field values of the selected content schema are automatically filled in. Only one schema can be selected.`,
                    )}
                    searchPlaceholder={t(
                        `common::msg::Search by custom metadata name.`,
                        `Search by custom metadata name.`,
                    )}
                    onClickSubmit={(contentSchemaDetail) => setEditContentSchemaDetail(contentSchemaDetail)}
                    onClickClose={closeDialog}
                />
            )}
        </>
    );
};

PanelPromptPresetResponseFormat.propTypes = {
    data: PropTypes.object,
    projectId: PropTypes.string,
    isEditButtonDisabled: PropTypes.bool,
    onSubmit: PropTypes.func.isRequired,
};

export default PanelPromptPresetResponseFormat;
